import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Sparkles, FileSpreadsheet, TrendingUp, Check } from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import { getRedditLanding } from "../lib/redditLandings";

export default function RedditLandingPage() {
  const { slug } = useParams();
  const data = getRedditLanding(slug);

  useEffect(() => {
    if (!data) return;
    document.title = `${data.headline} — CFO Grade for r/${data.sub}`;
    const desc = data.pitch;

    function upsert(attr, key, value) {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    }

    upsert("name", "description", desc);
    upsert("property", "og:title", `${data.headline} — CFO Grade`);
    upsert("property", "og:description", desc);
    upsert("property", "og:url", `${window.location.origin}/r/${slug}`);
    upsert("name", "twitter:title", `${data.headline} — CFO Grade`);
    upsert("name", "twitter:description", desc);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${window.location.origin}/r/${slug}`);
  }, [data, slug]);

  if (!data) {
    return <Navigate to="/" replace />;
  }

  // CTA preserves industry + UTM params so AnalyzePage can pre-fill
  const analyzeHref = `/analyze?industry=${encodeURIComponent(
    data.industryKey
  )}&utm_source=reddit&utm_medium=landing&utm_campaign=${encodeURIComponent(data.sub)}`;

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain relative" data-testid="reddit-landing">
      <header className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link to="/leaderboard" className="text-sm text-[#5C665D] hover:text-[#1F382E] transition-colors">
          Industry benchmarks →
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 pt-6 pb-20">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* LEFT — pitch */}
          <div className="lg:col-span-3">
            <div
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C26D5C] bg-[#C26D5C]/10 border border-[#C26D5C]/30 rounded-full px-3 py-1.5"
              data-testid="reddit-greeting"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#C26D5C]" />
              {data.greeting}
            </div>

            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F382E] leading-[1.05] tracking-tight mt-5">
              {data.headline}
            </h1>

            <p className="text-base sm:text-lg text-[#1A1A1A] leading-relaxed mt-6 max-w-xl">
              {data.pitch}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to={analyzeHref}>
                <Button
                  className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-12 px-7 text-base transition-all duration-300"
                  data-testid="reddit-cta-analyze"
                >
                  Run a free analysis
                  <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
                </Button>
              </Link>
              <Link to="/leaderboard">
                <Button
                  variant="ghost"
                  className="h-12 px-5 text-[#1F382E] hover:bg-[#E5E0D8]/40 rounded-md text-base"
                  data-testid="reddit-cta-benchmarks"
                >
                  See industry benchmarks
                </Button>
              </Link>
            </div>

            <ul className="mt-10 space-y-3">
              {data.proofPoints.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-[15px] text-[#1A1A1A] leading-relaxed"
                >
                  <Check
                    size={16}
                    strokeWidth={2}
                    className="mt-0.5 shrink-0 text-[#2C5C43]"
                  />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-6 mt-10 text-xs text-[#5C665D]">
              <div className="flex items-center gap-1.5">
                <Sparkles size={13} strokeWidth={1.5} /> Free analysis
              </div>
              <div className="flex items-center gap-1.5">
                <FileSpreadsheet size={13} strokeWidth={1.5} /> CSV / Excel upload
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingUp size={13} strokeWidth={1.5} /> Industry benchmarks
              </div>
            </div>
          </div>

          {/* RIGHT — benchmark teaser card */}
          <aside className="lg:col-span-2">
            <div
              className="bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-7 shadow-sm"
              data-testid="reddit-benchmark-card"
            >
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                {data.benchmarkTeaser.label}
              </p>
              <ul className="mt-5 divide-y divide-[#E5E0D8]">
                {data.benchmarkTeaser.rows.map((row) => (
                  <li
                    key={row.k}
                    className="flex items-center justify-between py-3 text-sm"
                  >
                    <span className="text-[#5C665D]">{row.k}</span>
                    <span className="font-medium text-[#1F382E] tabular-nums">{row.v}</span>
                  </li>
                ))}
              </ul>
              <Link to={analyzeHref} className="block mt-6">
                <Button
                  variant="outline"
                  className="w-full border-[#1F382E]/20 text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] rounded-md h-11 transition-all duration-300"
                  data-testid="reddit-cta-compare"
                >
                  Compare your numbers
                  <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
                </Button>
              </Link>
            </div>

            <p className="text-[11px] text-[#5C665D] leading-relaxed mt-4 px-1">
              Benchmarks are mid-range estimates for the sector and may not reflect your specific
              context. No signup required — your numbers stay on this device unless you opt in to
              anonymized peer benchmarking.
            </p>
          </aside>
        </div>
      </main>

      <SiteFootnote />
    </div>
  );
}
